import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { mandantenAPI } from '../lib/api';

/**
 * Pflegt die DATEV-Stammdaten des Mandanten (Berater-Nr, Mandant-Nr,
 * WJ-Beginn, Personenkonten-Basis). Pflicht vor dem ersten Voll-Export.
 */
export default function MandantSettingsModal({ open, onClose, mandant, onSaved }) {
  const [berater, setBerater] = useState('');
  const [mandantNr, setMandantNr] = useState('');
  const [wjBeginn, setWjBeginn] = useState('');
  const [debitorBasis, setDebitorBasis] = useState('10000');
  const [kreditorBasis, setKreditorBasis] = useState('70000');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mandant) return;
    setBerater(mandant.datev_berater_nr != null ? String(mandant.datev_berater_nr) : '');
    setMandantNr(mandant.datev_mandant_nr != null ? String(mandant.datev_mandant_nr) : '');
    setWjBeginn(mandant.datev_wj_beginn || `${new Date().getFullYear()}-01-01`);
    setDebitorBasis(String(mandant.datev_debitor_basis ?? 10000));
    setKreditorBasis(String(mandant.datev_kreditor_basis ?? 70000));
  }, [mandant, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!mandant) return;
    setBusy(true);
    try {
      await mandantenAPI.patch(mandant.slug, {
        datev_berater_nr: Number(berater),
        datev_mandant_nr: Number(mandantNr),
        datev_wj_beginn: wjBeginn,
        datev_debitor_basis: Number(debitorBasis),
        datev_kreditor_basis: Number(kreditorBasis),
      });
      toast.success('DATEV-Einstellungen gespeichert');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (!mandant) return null;

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to">
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>
        <div className="modal-positioner">
          <Transition.Child as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to">
            <Dialog.Panel className="modal-panel" style={{ width: 520 }}>
              <form onSubmit={submit}>
                <div className="modal-header">
                  <Dialog.Title className="modal-title">
                    DATEV-Einstellungen — {mandant.name}
                  </Dialog.Title>
                  <button type="button" className="btn-text" onClick={onClose}><X size={16} /></button>
                </div>

                <p className="muted small" style={{ marginTop: 8 }}>
                  Diese Werte landen im EXTF-Header jeder Export-Datei.
                  Ohne sie ist kein DATEV-/Stotax-Voll-Export möglich.
                </p>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <label>
                    Berater-Nr *
                    <input type="number" min="1" max="9999999" required
                      value={berater} onChange={(e) => setBerater(e.target.value)}
                      placeholder="z.B. 12345" />
                    <span className="muted small">Kanzlei-Nr in DATEV (1—7 Stellen)</span>
                  </label>
                  <label>
                    Mandant-Nr *
                    <input type="number" min="1" max="99999" required
                      value={mandantNr} onChange={(e) => setMandantNr(e.target.value)}
                      placeholder="z.B. 67890" />
                    <span className="muted small">Lfd. Nr in der Kanzlei (1—5 Stellen)</span>
                  </label>
                  <label style={{ gridColumn: '1 / span 2' }}>
                    Wirtschaftsjahr-Beginn *
                    <input type="date" required
                      value={wjBeginn} onChange={(e) => setWjBeginn(e.target.value)} />
                    <span className="muted small">Üblich der 01.01. des aktuellen Geschäftsjahres</span>
                  </label>
                  <label>
                    Debitoren-Basis
                    <input type="number" min="10000" max="69999"
                      value={debitorBasis} onChange={(e) => setDebitorBasis(e.target.value)} />
                    <span className="muted small">Kunde-ID + Basis = Personenkonto</span>
                  </label>
                  <label>
                    Kreditoren-Basis
                    <input type="number" min="70000" max="99999"
                      value={kreditorBasis} onChange={(e) => setKreditorBasis(e.target.value)} />
                    <span className="muted small">Lieferant-ID + Basis = Personenkonto</span>
                  </label>
                </div>

                <div className="actions" style={{ borderTop: 'none', paddingTop: 16 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary" disabled={busy}>
                    {busy ? 'Speichert…' : 'Speichern'}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
